import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, RefreshCw, Plus, Pencil, Trash2, Check, Eye, EyeOff, Clock, X, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import Seo from '../components/Seo';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUSES = ['pending', 'draft', 'published', 'scheduled', 'unpublished'];
const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  draft: 'bg-stone-100 text-stone-600',
  published: 'bg-emerald-100 text-emerald-700',
  scheduled: 'bg-blue-100 text-blue-700',
  unpublished: 'bg-red-100 text-red-600'
};

const EMPTY_ARTICLE = {
  title: '', category: 'Property News', locations: [], summary: '', content: '',
  insight: '', source_name: 'Versatile Geopick', source_url: '', source_date: '',
  featured_image: '', keywords: [], seo_title: '', seo_description: ''
};

const EMPTY_DEV = {
  name: '', location: '', type: '', description: '', latest_update: '',
  date_updated: '', source: '', considerations: '', status: 'published'
};

const Admin = () => {
  const { toast } = useToast();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [tab, setTab] = useState('articles');
  const [articles, setArticles] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [developments, setDevelopments] = useState([]);
  const [meta, setMeta] = useState({ categories: [], locations: [] });
  const [editing, setEditing] = useState(null); // article being edited (or EMPTY_ARTICLE for new)
  const [editingDev, setEditingDev] = useState(null);
  const [scheduleFor, setScheduleFor] = useState(null); // article id being scheduled
  const [scheduleDate, setScheduleDate] = useState('');
  const [importing, setImporting] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('geopick_admin_token');
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  const loadArticles = useCallback(async () => {
    const params = statusFilter ? `?status=${statusFilter}` : '';
    const res = await fetch(`${API}/api/hub/admin/articles${params}`, { headers: authHeaders(), credentials: 'include' });
    if (res.status === 401) { setUser(null); return; }
    const data = await res.json();
    setArticles(data.articles || []);
    setCounts(data.counts || {});
  }, [statusFilter]);

  const loadDevelopments = useCallback(async () => {
    const res = await fetch(`${API}/api/hub/admin/developments`, { headers: authHeaders(), credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      setDevelopments(data.developments || []);
    }
  }, []);

  useEffect(() => {
    fetch(`${API}/api/hub/meta`).then((r) => r.json()).then(setMeta).catch(() => {});
    fetch(`${API}/api/auth/me`, { credentials: 'include', headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => { setUser(u); setChecking(false); })
      .catch(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (user) { loadArticles(); loadDevelopments(); }
  }, [user, loadArticles, loadDevelopments]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(loginForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Login failed');
      if (data.token) localStorage.setItem('geopick_admin_token', data.token);
      setUser(data);
      toast({ title: 'Welcome back', description: 'Logged in successfully.' });
    } catch (err) {
      setLoginError(err.message);
    }
  };

  const handleLogout = async () => {
    await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    localStorage.removeItem('geopick_admin_token');
    setUser(null);
  };

  const articleAction = async (id, action, extra = {}) => {
    const res = await fetch(`${API}/api/hub/admin/articles/${id}/action`, {
      method: 'POST', headers: authHeaders(), credentials: 'include',
      body: JSON.stringify({ action, ...extra })
    });
    if (res.ok) {
      toast({ title: 'Done', description: `Article ${action}d.` });
      setScheduleFor(null);
      loadArticles();
    } else {
      const d = await res.json();
      toast({ title: 'Action failed', description: d.detail || 'Try again', variant: 'destructive' });
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm('Delete this article permanently?')) return;
    const res = await fetch(`${API}/api/hub/admin/articles/${id}`, { method: 'DELETE', headers: authHeaders(), credentials: 'include' });
    if (res.ok) { toast({ title: 'Deleted' }); loadArticles(); }
  };

  const saveArticle = async (e) => {
    e.preventDefault();
    const isNew = !editing.id;
    const payload = { ...editing, keywords: typeof editing.keywords === 'string' ? editing.keywords.split(',').map((k) => k.trim()).filter(Boolean) : editing.keywords };
    const res = await fetch(`${API}/api/hub/admin/articles${isNew ? '' : `/${editing.id}`}`, {
      method: isNew ? 'POST' : 'PUT', headers: authHeaders(), credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (res.ok) {
      toast({ title: isNew ? 'Article created' : 'Article saved' });
      setEditing(null);
      loadArticles();
    } else {
      toast({ title: 'Save failed', variant: 'destructive' });
    }
  };

  const saveDev = async (e) => {
    e.preventDefault();
    const isNew = !editingDev.id;
    const res = await fetch(`${API}/api/hub/admin/developments${isNew ? '' : `/${editingDev.id}`}`, {
      method: isNew ? 'POST' : 'PUT', headers: authHeaders(), credentials: 'include',
      body: JSON.stringify(editingDev)
    });
    if (res.ok) { toast({ title: isNew ? 'Development added' : 'Development saved' }); setEditingDev(null); loadDevelopments(); }
  };

  const deleteDev = async (id) => {
    if (!window.confirm('Delete this development?')) return;
    const res = await fetch(`${API}/api/hub/admin/developments/${id}`, { method: 'DELETE', headers: authHeaders(), credentials: 'include' });
    if (res.ok) { toast({ title: 'Deleted' }); loadDevelopments(); }
  };

  const runImport = async () => {
    setImporting(true);
    const res = await fetch(`${API}/api/hub/admin/import`, { method: 'POST', headers: authHeaders(), credentials: 'include' });
    if (res.ok) {
      toast({ title: 'Import started', description: 'New stories will appear under Pending review in about a minute.' });
      setTimeout(loadArticles, 45000);
    } else {
      toast({ title: 'Import failed', variant: 'destructive' });
    }
    setImporting(false);
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-stone-500 pt-24">Loading...</div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-100 to-amber-50/30 flex items-center justify-center px-6 pt-24">
        <Seo title="Admin Login | Versatile Geopick" description="Administrator access." />
        <form onSubmit={handleLogin} data-testid="admin-login-form"
          className="bg-white rounded-3xl shadow-xl border border-stone-200 p-10 w-full max-w-md">
          <h1 className="text-3xl font-light text-stone-800 mb-2">Property Hub Admin</h1>
          <p className="text-stone-500 text-sm mb-8">Sign in to review and publish articles.</p>
          {loginError && <p className="text-red-600 text-sm mb-4" data-testid="login-error">{loginError}</p>}
          <div className="space-y-4">
            <Input type="email" placeholder="Email" data-testid="admin-email" required
              value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
              className="border-stone-300" />
            <Input type="password" placeholder="Password" data-testid="admin-password" required
              value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              className="border-stone-300" />
            <Button type="submit" data-testid="admin-login-submit"
              className="w-full bg-emerald-700 hover:bg-emerald-800 text-white py-6">Sign In</Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-24 pb-20">
      <Seo title="Property Hub Admin | Versatile Geopick" description="Manage Property Hub content." />
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-light text-stone-800" data-testid="admin-dashboard-title">Property Hub Admin</h1>
            <p className="text-stone-500 text-sm">Signed in as {user.email}</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={runImport} disabled={importing} data-testid="import-news-button"
              className="bg-emerald-700 hover:bg-emerald-800 text-white">
              <Download className="mr-2 h-4 w-4" /> {importing ? 'Importing...' : 'Import News Now'}
            </Button>
            <Button variant="outline" onClick={handleLogout} data-testid="admin-logout" className="border-stone-300">
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8">
          {['articles', 'developments'].map((t) => (
            <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
              className={`px-6 py-2.5 rounded-full text-sm font-medium capitalize transition-colors ${
                tab === t ? 'bg-emerald-700 text-white' : 'bg-white text-stone-600 border border-stone-200'
              }`}>
              {t}
            </button>
          ))}
        </div>

        {tab === 'articles' && (
          <>
            {/* Status filter + new */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <button data-testid="filter-all" onClick={() => setStatusFilter('')}
                className={`px-4 py-2 rounded-full text-sm ${!statusFilter ? 'bg-stone-800 text-white' : 'bg-white border border-stone-200'}`}>
                All ({Object.values(counts).reduce((a, b) => a + b, 0)})
              </button>
              {STATUSES.map((s) => (
                <button key={s} data-testid={`filter-${s}`} onClick={() => setStatusFilter(s)}
                  className={`px-4 py-2 rounded-full text-sm capitalize ${statusFilter === s ? 'bg-stone-800 text-white' : 'bg-white border border-stone-200'}`}>
                  {s} ({counts[s] || 0})
                </button>
              ))}
              <div className="flex-1" />
              <Button onClick={() => setEditing({ ...EMPTY_ARTICLE })} data-testid="new-article-button"
                variant="outline" className="border-emerald-600 text-emerald-700">
                <Plus className="mr-2 h-4 w-4" /> New Article
              </Button>
              <Button onClick={loadArticles} variant="ghost" data-testid="refresh-button"><RefreshCw className="h-4 w-4" /></Button>
            </div>

            {/* Articles list */}
            <div className="space-y-3" data-testid="admin-articles-list">
              {articles.length === 0 && <p className="text-stone-500 py-10 text-center">No articles in this view.</p>}
              {articles.map((a) => (
                <div key={a.id} data-testid={`admin-article-${a.id}`}
                  className="bg-white border border-stone-200 rounded-2xl p-5 flex flex-wrap items-center gap-4">
                  <img src={a.featured_image} alt="" className="w-20 h-14 object-cover rounded-xl flex-shrink-0" loading="lazy" />
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-medium text-stone-800 leading-snug">{a.title}</p>
                    <p className="text-xs text-stone-500 mt-1">
                      {a.category}{a.source_url ? ` · Source: ${a.source_name}` : ' · Original'}
                      {a.imported && ' · Imported'}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[a.status] || ''}`}
                    data-testid={`status-${a.id}`}>{a.status}</span>
                  <div className="flex gap-1.5 flex-wrap">
                    {(a.status === 'pending' || a.status === 'draft') && (
                      <button onClick={() => articleAction(a.id, 'approve')} data-testid={`approve-${a.id}`} title="Approve & Publish"
                        className="p-2 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200"><Check className="h-4 w-4" /></button>
                    )}
                    {a.status === 'published' && (
                      <button onClick={() => articleAction(a.id, 'unpublish')} data-testid={`unpublish-${a.id}`} title="Unpublish"
                        className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"><EyeOff className="h-4 w-4" /></button>
                    )}
                    {a.status === 'unpublished' && (
                      <button onClick={() => articleAction(a.id, 'publish')} data-testid={`publish-${a.id}`} title="Republish"
                        className="p-2 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200"><Eye className="h-4 w-4" /></button>
                    )}
                    <button onClick={() => { setScheduleFor(a.id); setScheduleDate(''); }} data-testid={`schedule-${a.id}`} title="Schedule"
                      className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100"><Clock className="h-4 w-4" /></button>
                    <button onClick={() => setEditing({ ...a, source_url: a.source_url || '', source_date: a.source_date || '', featured_image: a.featured_image || '' })}
                      data-testid={`edit-${a.id}`} title="Edit"
                      className="p-2 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => deleteArticle(a.id)} data-testid={`delete-${a.id}`} title="Delete"
                      className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"><Trash2 className="h-4 w-4" /></button>
                    {a.status === 'published' && (
                      <Link to={`/property-hub/${a.slug}`} target="_blank" title="View"
                        className="p-2 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200"><Eye className="h-4 w-4" /></Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === 'developments' && (
          <>
            <div className="flex justify-end mb-6">
              <Button onClick={() => setEditingDev({ ...EMPTY_DEV })} data-testid="new-development-button"
                variant="outline" className="border-emerald-600 text-emerald-700">
                <Plus className="mr-2 h-4 w-4" /> New Development
              </Button>
            </div>
            <div className="space-y-3" data-testid="admin-developments-list">
              {developments.length === 0 && <p className="text-stone-500 py-10 text-center">No developments yet.</p>}
              {developments.map((d) => (
                <div key={d.id} data-testid={`admin-dev-${d.id}`}
                  className="bg-white border border-stone-200 rounded-2xl p-5 flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-medium text-stone-800">{d.name}</p>
                    <p className="text-xs text-stone-500 mt-1">{d.location} · {d.type}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${d.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-600'}`}>
                    {d.status}
                  </span>
                  <div className="flex gap-1.5">
                    <button onClick={() => setEditingDev({ ...d })} data-testid={`edit-dev-${d.id}`}
                      className="p-2 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => deleteDev(d.id)} data-testid={`delete-dev-${d.id}`}
                      className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Schedule modal */}
      {scheduleFor && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="schedule-modal">
          <div className="bg-white rounded-3xl p-8 w-full max-w-md">
            <h3 className="text-xl font-medium text-stone-800 mb-4">Schedule Publication</h3>
            <Input type="datetime-local" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)}
              data-testid="schedule-datetime" className="border-stone-300 mb-6" />
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setScheduleFor(null)} className="flex-1 border-stone-300">Cancel</Button>
              <Button onClick={() => articleAction(scheduleFor, 'schedule', { scheduled_at: new Date(scheduleDate).toISOString() })}
                disabled={!scheduleDate} data-testid="confirm-schedule"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">Schedule</Button>
            </div>
          </div>
        </div>
      )}

      {/* Article editor modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6 overflow-y-auto" data-testid="article-editor-modal">
          <form onSubmit={saveArticle} className="bg-white rounded-3xl p-8 w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-light text-stone-800">{editing.id ? 'Edit Article' : 'New Article'}</h3>
              <button type="button" onClick={() => setEditing(null)} data-testid="close-editor"><X className="h-5 w-5 text-stone-500" /></button>
            </div>
            <div className="space-y-4">
              <Input placeholder="Headline *" value={editing.title} required data-testid="edit-title"
                onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="border-stone-300" />
              <div className="grid md:grid-cols-2 gap-4">
                <select value={editing.category} data-testid="edit-category"
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="px-4 py-3 border border-stone-300 rounded-xl bg-white">
                  {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <Input placeholder="Featured image URL" value={editing.featured_image} data-testid="edit-image"
                  onChange={(e) => setEditing({ ...editing, featured_image: e.target.value })} className="border-stone-300" />
              </div>
              <div>
                <p className="text-xs text-stone-500 mb-2">Locations (click to toggle)</p>
                <div className="flex flex-wrap gap-2">
                  {meta.locations.map((loc) => (
                    <button type="button" key={loc}
                      onClick={() => setEditing({ ...editing, locations: editing.locations.includes(loc) ? editing.locations.filter((l) => l !== loc) : [...editing.locations, loc] })}
                      className={`px-3 py-1 rounded-full text-xs ${editing.locations.includes(loc) ? 'bg-emerald-700 text-white' : 'bg-stone-100 text-stone-600'}`}>
                      {loc}
                    </button>
                  ))}
                </div>
              </div>
              <Textarea placeholder="Short summary *" value={editing.summary} required rows={2} data-testid="edit-summary"
                onChange={(e) => setEditing({ ...editing, summary: e.target.value })} className="border-stone-300" />
              <Textarea placeholder="Article content (paragraphs separated by blank lines)" value={editing.content} rows={6} data-testid="edit-content"
                onChange={(e) => setEditing({ ...editing, content: e.target.value })} className="border-stone-300" />
              <Textarea placeholder="Versatile Geopick Insight — what this means from a surveying/land/property perspective" value={editing.insight} rows={3} data-testid="edit-insight"
                onChange={(e) => setEditing({ ...editing, insight: e.target.value })} className="border-stone-300" />
              <div className="grid md:grid-cols-3 gap-4">
                <Input placeholder="Source name" value={editing.source_name} data-testid="edit-source-name"
                  onChange={(e) => setEditing({ ...editing, source_name: e.target.value })} className="border-stone-300" />
                <Input placeholder="Source URL" value={editing.source_url} data-testid="edit-source-url"
                  onChange={(e) => setEditing({ ...editing, source_url: e.target.value })} className="border-stone-300" />
                <Input placeholder="Source date" value={editing.source_date} data-testid="edit-source-date"
                  onChange={(e) => setEditing({ ...editing, source_date: e.target.value })} className="border-stone-300" />
              </div>
              <Input placeholder="SEO keywords (comma separated)" data-testid="edit-keywords"
                value={Array.isArray(editing.keywords) ? editing.keywords.join(', ') : editing.keywords}
                onChange={(e) => setEditing({ ...editing, keywords: e.target.value })} className="border-stone-300" />
              <div className="grid md:grid-cols-2 gap-4">
                <Input placeholder="SEO title (max 60 chars)" value={editing.seo_title || ''} maxLength={60} data-testid="edit-seo-title"
                  onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })} className="border-stone-300" />
                <Input placeholder="Meta description (max 155 chars)" value={editing.seo_description || ''} maxLength={155} data-testid="edit-seo-description"
                  onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })} className="border-stone-300" />
              </div>
              <Button type="submit" data-testid="save-article" className="w-full bg-emerald-700 hover:bg-emerald-800 text-white py-6">
                {editing.id ? 'Save Changes' : 'Create Draft'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Development editor modal */}
      {editingDev && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="dev-editor-modal">
          <form onSubmit={saveDev} className="bg-white rounded-3xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-light text-stone-800">{editingDev.id ? 'Edit Development' : 'New Development'}</h3>
              <button type="button" onClick={() => setEditingDev(null)}><X className="h-5 w-5 text-stone-500" /></button>
            </div>
            <div className="space-y-4">
              <Input placeholder="Project/development name *" value={editingDev.name} required data-testid="dev-name"
                onChange={(e) => setEditingDev({ ...editingDev, name: e.target.value })} className="border-stone-300" />
              <div className="grid md:grid-cols-2 gap-4">
                <Input placeholder="Location *" value={editingDev.location} required data-testid="dev-location"
                  onChange={(e) => setEditingDev({ ...editingDev, location: e.target.value })} className="border-stone-300" />
                <Input placeholder="Type (e.g. Road, Rail, Estate, Port) *" value={editingDev.type} required data-testid="dev-type"
                  onChange={(e) => setEditingDev({ ...editingDev, type: e.target.value })} className="border-stone-300" />
              </div>
              <Textarea placeholder="Description" value={editingDev.description} rows={3} data-testid="dev-description"
                onChange={(e) => setEditingDev({ ...editingDev, description: e.target.value })} className="border-stone-300" />
              <Textarea placeholder="Latest update" value={editingDev.latest_update} rows={2} data-testid="dev-update"
                onChange={(e) => setEditingDev({ ...editingDev, latest_update: e.target.value })} className="border-stone-300" />
              <div className="grid md:grid-cols-2 gap-4">
                <Input type="date" value={(editingDev.date_updated || '').slice(0, 10)} data-testid="dev-date"
                  onChange={(e) => setEditingDev({ ...editingDev, date_updated: e.target.value })} className="border-stone-300" />
                <Input placeholder="Source" value={editingDev.source} data-testid="dev-source"
                  onChange={(e) => setEditingDev({ ...editingDev, source: e.target.value })} className="border-stone-300" />
              </div>
              <Textarea placeholder="Relevant property considerations" value={editingDev.considerations} rows={2} data-testid="dev-considerations"
                onChange={(e) => setEditingDev({ ...editingDev, considerations: e.target.value })} className="border-stone-300" />
              <Button type="submit" data-testid="save-development" className="w-full bg-emerald-700 hover:bg-emerald-800 text-white py-6">
                Save Development
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Admin;
